import React from 'react';
import { useAuthStore } from '@/store/authStore';
import { InlineLoader, EmptyState } from '@/components/ui';
import { useDocuments } from '../hooks/useDocuments';
import { DocumentsHero } from '../components/DocumentsHero';
import { DocumentsStats } from '../components/DocumentsStats';
import { DocumentsFilterSection } from '../components/DocumentsFilterSection';
import { DocumentSectionHeader } from '../components/DocumentSectionHeader';
import { DocumentsTableView } from '../components/DocumentsTableView';
import { DocumentModals } from '../components/DocumentModals';
import { DOCUMENT_SECTIONS } from '../constants/documentConstants';

export default function DocumentsScreen() {
  const { user } = useAuthStore();
  const isOwner = user?.role === 'owner';

  const doc = useDocuments(user);

  if (doc.loading && !doc.documents.length) {
    return (
      <div className="flex items-center justify-center h-96">
        <InlineLoader text="Loading documents..." />
      </div>
    );
  }

  const sections = DOCUMENT_SECTIONS.filter((s) => {
    if (doc.formatFilter !== 'all' && s.format !== doc.formatFilter) {
      return false;
    }
    return s.id !== 'other' || doc.groupedDocs.other.length > 0;
  });

  return (
    <div className="flex flex-col gap-6 w-full max-w-full pb-2 overflow-x-hidden">
      <DocumentsHero />

      <DocumentsStats
        counts={doc.statsCounts}
        isExpanded={doc.isStatsExpanded}
        onToggle={() => doc.setIsStatsExpanded(!doc.isStatsExpanded)}
      />
{/*  Updated Halimunnisa 09-06-2026
Added a filter section to allow users to easily search and filter their documents, improving usability and efficiency in managing larger document libraries. Also added an export button to allow users to export their documents list with applied filters, enhancing data management capabilities. */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-col rounded-2xl overflow-hidden bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] min-w-0">
          <DocumentsFilterSection
            search={doc.search}
            setSearch={doc.setSearch}
            formatFilter={doc.formatFilter}
            setFormatFilter={doc.setFormatFilter}
            filters={doc.filters}
            setFilters={(key, val) => doc.setFilters(prev => ({ ...prev, [key]: val }))}
            onAddClick={() => doc.setShowUploadModal(true)}
            isOwner={isOwner}
            onExport={doc.handleExport}
            isExporting={doc.isExporting}
          />
        </div>

        {doc.loading ? (
          <div className="flex justify-center py-16">
            <InlineLoader text="Refreshing..." />
          </div>
        ) : doc.documents.length === 0 ? (
          <EmptyState
            icon="📁"
            title="No documents found"
            description="Upload your first document to get started."
          />
        ) : (
          <div className="space-y-3">
            {sections.map((section) => {
              const sectionDocs = doc.groupedDocs[section.id] || [];
              const isExpanded = doc.expandedSection === section.id;
              return (
                <div key={section.id} className={`bg-white rounded-xl border transition-all duration-300 shadow-[0_8px_30px_rgb(0,0,0,0.04)] ${isExpanded ? 'border-slate-800' : 'border-slate-200/60 hover:shadow-[0_12px_35px_rgba(0,0,0,0.08)]'} overflow-hidden`}>
                  <DocumentSectionHeader
                    id={section.id}
                    title={section.title}
                    count={sectionDocs.length}
                    colorClass={section.colorClass}
                    isExpanded={isExpanded}
                    onToggle={() => doc.setExpandedSection(isExpanded ? null : section.id)}
                  />
                  {isExpanded && (
                    <div className="animate-in slide-in-from-top-2 duration-300 border-t border-slate-200">
                      <DocumentsTableView
                        docs={sectionDocs}
                        onOpen={doc.openDocument}
                        onPermissions={(d) => {
                          doc.setSelectedDoc(d);
                          doc.setShowPermissionModal(true);
                        }}
                        onDownload={doc.handleDownloadFile}
                        onDelete={doc.handleDelete}
                        canEdit={doc.canEdit}
                        isOwner={isOwner}
                        apiBaseUrl={doc.apiBaseUrl}
                        sectionId={section.id}
                      />
                    </div>
                  )}
                </div>
              );
            })}
            {doc.filteredDocs.length === 0 && doc.documents.length > 0 && (
              <EmptyState
                icon="🔍"
                title="No matching documents"
                description="Try a different search or format filter."
              />
            )}
          </div>
        )}
      </div>

      <DocumentModals
        showCreateModal={doc.showCreateModal}
        setShowCreateModal={doc.setShowCreateModal}
        createType={doc.createType}
        createTitle={doc.createTitle}
        setCreateTitle={doc.setCreateTitle}
        creating={doc.creating}
        handleCreateBlank={doc.handleCreateBlank}
        showUploadModal={doc.showUploadModal}
        setShowUploadModal={doc.setShowUploadModal}
        uploadFile={doc.uploadFile}
        setUploadFile={doc.setUploadFile}
        uploadTitle={doc.uploadTitle}
        setUploadTitle={doc.setUploadTitle}
        uploadCategory={doc.uploadCategory}
        setUploadCategory={doc.setUploadCategory}
        isRelatedToCatalog={doc.isRelatedToCatalog}
        setIsRelatedToCatalog={doc.setIsRelatedToCatalog}
        uploadNotes={doc.uploadNotes}
        setUploadNotes={doc.setUploadNotes}
        handleUpload={doc.handleUpload}
        setCreateType={doc.setCreateType}
        uploading={doc.uploading}
        showPreviewModal={doc.showPreviewModal}
        setShowPreviewModal={doc.setShowPreviewModal}
        selectedDoc={doc.selectedDoc}
        editTitle={doc.editTitle}
        setEditTitle={doc.setEditTitle}
        handleUpdate={doc.handleUpdate}
        excelData={doc.excelData}
        canEdit={doc.canEdit}
        user={doc.user}
        apiBaseUrl={doc.apiBaseUrl}
        showPermissionModal={doc.showPermissionModal}
        setShowPermissionModal={doc.setShowPermissionModal}
        allUsers={doc.allUsers}
        handleAddUser={doc.handleAddUser}
        handleUpdatePermissions={doc.handleUpdatePermissions}
        fetchDocuments={doc.fetchDocuments}
        setSelectedDoc={doc.setSelectedDoc}
        showDeleteModal={doc.showDeleteModal}
        setShowDeleteModal={doc.setShowDeleteModal}
        confirmDelete={doc.confirmDelete}
        docToDeleteId={doc.docToDeleteId}
        setDocToDeleteId={doc.setDocToDeleteId}
        deleting={doc.deleting}
      />
    </div>
  );
}
